import React, { useCallback, useRef, useState } from 'react';

import { useClassName, useScrollBlock, useWindowSize } from 'common/hooks';
import { ContentfulModularForm } from 'public/components/ModularComponents';

import Breadcrumbs from './Components/Breadcrumbs';
import Hero from './Components/Hero';
import Header from './Components/Header';
import Carousel from './Components/Carousel';
import List from './Components/List';
import Gallery from './Components/Gallery';
import Faqs from './Components/FAQs';
import useCustomStyle from './hooks';
import VenueDetails from './Components/Venue/Details';
import AvenueDetails from './Components/Venue/Details/AvenueDetails';
import LPFrame from './Components/Frame';
import Form from './Components/Form';
import Subscribe from './Components/Custom/Subscribe';
import AvenueSubscribe from './Components/Custom/Subscribe/AvenueSubscribe';
import PropertyInfo from './Components/PropertyInfo/PropertyInfo';
import RetailDeals from './Components/RetailDeals';
import SectionLink from './Components/SectionLink';
import CollapsibleList from './Components/CollapsibleList';
import Stacking from './Components/Stacking';
import VerticalCarousel from './Components/VerticalCarousel';
import Banner from './Components/Banner';
import CLPContentBanner from './Components/ContentBanner';

import './section.less';
import { ga4Track } from 'utils/analytics/trackV2';
import FaqsList from './Components/FaqsList/FaqsList';

const CONTENT_BANNER_TYPE = 'B';

function hasValue(value) {
  return value !== undefined && value !== null && value !== '';
}

function getResponsiveValue(fields, viewport) {
  if (viewport.isMobile && hasValue(fields.mobileValue)) {
    return fields.mobileValue;
  }

  if (viewport.isTablet && hasValue(fields.tabletValue)) {
    return fields.tabletValue;
  }

  if (viewport.isDesktop && hasValue(fields.desktopValue)) {
    return fields.desktopValue;
  }

  if (hasValue(fields.desktopValue)) return fields.desktopValue;
  if (hasValue(fields.tabletValue)) return fields.tabletValue;
  if (hasValue(fields.mobileValue)) return fields.mobileValue;
  if (hasValue(fields.value)) return fields.value;

  return undefined;
}

function getResponsiveAssetField(asset, viewport) {
  if (!asset?.fields) return null;

  if (viewport.isMobile && asset.fields.mobile) return asset.fields.mobile;
  if (viewport.isTablet && asset.fields.tablet) return asset.fields.tablet;
  if (viewport.isDesktop && asset.fields.desktop) return asset.fields.desktop;

  return asset.fields.desktop || asset.fields.tablet || asset.fields.mobile;
}

function normalizeBackgroundSettingKey(key) {
  if (!key) return null;
  if (key === '--background') return key;
  if (key.startsWith('--background-')) {
    return key.replace('--background-', 'background-');
  }
  return key;
}

function getSectionBackgroundStyles(background, settings, viewport) {
  const assetField = getResponsiveAssetField(background, viewport);
  const assetUrl = assetField?.fields?.file?.url;

  return (settings || []).reduce(
    (acc, setting) => {
      const rawKey = setting?.fields?.key || setting?.fields?.name;
      const key = normalizeBackgroundSettingKey(rawKey);
      const value = getResponsiveValue(setting?.fields || {}, viewport);

      if (
        !rawKey ||
        !rawKey.startsWith('--background-') ||
        !key ||
        !hasValue(value) ||
        key === '--background-image'
      ) {
        return acc;
      }

      acc[key] = value;
      return acc;
    },
    {
      ...(assetUrl && { '--background': `url(${assetUrl})` }),
    }
  );
}

function SectionContainer({
  index,
  id,
  refs,
  settings,
  background,
  viewport,
  children,
}) {
  const classNames = useClassName('CLPSection');
  const [setRef, style] = useCustomStyle(settings);
  const isModal = style['--is-modal-section'] || false;

  // Reset key variables to prevent inheritance from parent sections
  const safeStyle = {
    '--primary-color': 'transparent',
    '--secondary-color': 'transparent',
    '--background': 'transparent',
    ...style,
  };
  const [blockScroll, allowScroll] = useScrollBlock();
  const backgroundStyle = getSectionBackgroundStyles(
    background,
    settings,
    viewport
  );
  const previousBackgroundKeysRef = useRef([]);

  const handleCloseModal = (ref) => ref.classList.remove('open');

  const attachModalObserver = (node) => {
    if (!node || !isModal) return;
    const handleScrollLock = () => {
      if (node.classList.contains('open')) {
        blockScroll();
      } else {
        allowScroll();
      }
    };
    const observer = new MutationObserver(() => handleScrollLock());
    observer.observe(node, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  };

  return (
    <div
      className={classNames('container', isModal ? 'modal' : 'default')}
      style={safeStyle}
      ref={(node) => {
        if (node && refs.current) {
          refs.current[index] = node;
          setRef(node);
          previousBackgroundKeysRef.current.forEach((key) => {
            node.style.removeProperty(key);
          });
          Object.entries(backgroundStyle).forEach(([key, value]) => {
            node.style.setProperty(key, value, 'important');
          });
          previousBackgroundKeysRef.current = Object.keys(backgroundStyle);
          attachModalObserver(node);
        }
      }}
      id={id}>
      {isModal && (
        <button
          onClick={() => handleCloseModal(refs.current[index])}
          className={classNames('close-modal')}>
          x
        </button>
      )}
      {children}
    </div>
  );
}

export default function Section({ section, rawSection, refs, index, venue }) {
  const { isMobile, isTablet, isDesktop } = useWindowSize();
  const components = section.fields.components || [];
  // Get raw (wrapped) components for form content type
  const rawComponents = rawSection?.fields?.components?.['en-US'] || [];
  const breadcrumbs = components.find(
    (component) => component.sys.contentType.sys.id === 'lpBreadcrumbs'
  );

  const hasVerticalCarousel = components.some(
    (c) => c.sys.contentType.sys.id === 'lpVerticalCarousel'
  );
  const [carouselTravel, setCarouselTravel] = useState(0);

  const renderComponent = useCallback(
    (component, idx) => {
      const func = {
        lpCard: () => Hero,
        lpHeader: () => Header,
        lpCarousel: () => Carousel,
        lpList: () => List,
        lpCollapsibleList: () => CollapsibleList,
        lpFrame: () => LPFrame,
        lpGallery: () => Gallery,
        lpFaqs: () => Faqs,
        lpLink: () => SectionLink,
        lpStacking: () => Stacking,
        lpVerticalCarousel: () => VerticalCarousel,
        lpCustomComponent: () => {
          return {
            'Venue Detail': VenueDetails,
            'Avenue Detail': AvenueDetails,
            'Avenue Subscribe': AvenueSubscribe,
            'Property Info': PropertyInfo,
            Subscribe: Subscribe,
            'Retail Deals': RetailDeals,
          }[component.fields.type];
        },
        lpForm: () => Form,
        lpBanner: () =>
          component.fields.type?.toUpperCase() === CONTENT_BANNER_TYPE
            ? CLPContentBanner
            : Banner,
        lpFaqsList: () => FaqsList,
        lpGrid: () => {
          const Grid = require('./Components/Grid').default;
          // Pass Section component as prop to Grid to break circular dependency
          return (props) => <Grid {...props} SectionComponent={Section} />;
        },
        form: () => {
          // Use raw (wrapped) component for ModularForm compatibility
          const rawComponent = rawComponents[idx];
          return () => <ContentfulModularForm form={rawComponent} />;
        },
      }[component.sys.contentType.sys.id];

      if (!func) return null;
      const Component = func();

      if (!Component) return null;
      return (
        <Component
          component={component}
          key={idx}
          refs={refs}
          venue={venue}
          handleTrack={({ event, params }) =>
            handleTrack({
              sectionName: section.fields.name,
              event,
              params,
            })
          }
          {...(component.sys.contentType.sys.id === 'lpVerticalCarousel' && {
            onTravelChange: setCarouselTravel,
          })}
        />
      );
    },
    [refs, venue]
  );

  const renderResponsiveComponent = useCallback(
    (component) => {
      const { mobile, tablet, desktop } = component.fields;

      const variant =
        (isMobile && mobile) || (isTablet && tablet) || (isDesktop && desktop);
      if (!variant) return null;

      return variant?.map((inner) => renderComponent(inner, inner.sys.id));
    },
    [isMobile, isTablet, isDesktop, renderComponent]
  );

  const handleTrack = ({ sectionName, event, params }) => {
    ga4Track({
      event,
      params: {
        page_type: 'landing',
        section: sectionName,
        ...params,
      },
    });
  };

  const renderedComponents = section.fields.components.map((component, idx) => {
    if (component.sys.contentType.sys.id === 'lpResponsive') {
      return renderResponsiveComponent(component);
    }
    return renderComponent(component, idx);
  });

  const sectionElement = (
    <SectionContainer
      settings={section.fields.settings}
      background={section.fields.background}
      viewport={{ isMobile, isTablet, isDesktop }}
      id={section.fields.sectionId}
      index={index}
      refs={refs}>
      {renderedComponents}
    </SectionContainer>
  );

  return (
    <>
      {hasVerticalCarousel ? (
        <div className="clp-section__carousel-scene">
          {sectionElement}
          {carouselTravel > 0 && (
            <div
              className="clp-section__carousel-spacer"
              style={{ height: carouselTravel }}
            />
          )}
        </div>
      ) : (
        sectionElement
      )}
      {Boolean(breadcrumbs) && (
        <Breadcrumbs
          component={breadcrumbs}
          refs={refs}
          handleTrack={({ event, params }) =>
            handleTrack({
              sectionName: section.fields.name,
              event,
              params,
            })
          }
        />
      )}
    </>
  );
}
